// import React, { useState, useEffect } from 'react';
// import { Navigate, useLocation } from 'react-router-dom';
// import { useSelector, useDispatch } from 'react-redux';
// import { clearAuth, getToken, isTokenExpired } from '../utils/auth';
// import { resetAuthState } from '../redux/slices/authSlice';


// const ProtectedRoute = ({ children }) => {
//   const location = useLocation();
//   const dispatch = useDispatch();

//   // Perform SYNCHRONOUS initial auth check to prevent flash
//   // This runs during component initialization, before first render
//   const getInitialAuthState = () => {
//     const localToken = getToken();
//     if (!localToken) {
//       return { checking: false, valid: false };
//     }
//     const expired = isTokenExpired(localToken);
//     if (expired) {
//       return { checking: false, valid: false };
//     }
//     return { checking: false, valid: true };
//   };

//   const initialState = getInitialAuthState();

//   // Initialize with the result of synchronous check - no flash!
//   const [isChecking, setIsChecking] = useState(initialState.checking);
//   const [isValid, setIsValid] = useState(initialState.valid);

//   // Get auth token from Redux state
//   const { token: reduxToken } = useSelector((state) => state.auth);

//   // Re-check authentication when location changes (for navigation within the app)
//   useEffect(() => {
//     const checkAuth = () => {
//       // Check localStorage for token
//       const localToken = getToken();

//       if (!localToken) {
//         // No token found - clear auth and mark as invalid
//         clearAuth();
//         dispatch(resetAuthState());
//         setIsValid(false);
//         setIsChecking(false);
//         return;
//       }

//       // Check if token is expired
//       const expired = isTokenExpired(localToken);

//       if (expired) {
//         // Token expired - clear auth and mark as invalid
//         clearAuth();
//         dispatch(resetAuthState());
//         setIsValid(false);
//         setIsChecking(false);
//         return;
//       }

//       // Token is valid
//       setIsValid(true);
//       setIsChecking(false);
//     };

//     checkAuth();
//   }, [dispatch, location.pathname]);

//   // Show a minimal loading indicator while checking auth (prevents flash)
//   if (isChecking) {
//     return (
//       <div style={{
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//         height: '100vh',
//         backgroundColor: '#f8f9fa'
//       }}>
//         <div className="spinner-border text-primary" role="status">
//           <span className="visually-hidden">Loading...</span>
//         </div>
//       </div>
//     );
//   }

//   // If not valid, redirect to login
//   if (!isValid) {
//     return <Navigate to="/login" replace state={{ from: location }} />;
//   }

//   // User has valid token - render protected component
//   return children;
// };

// export default ProtectedRoute;
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearAuth, getToken, getRefreshToken, isTokenExpired } from '../utils/auth';
import { resetAuthState } from '../redux/slices/authSlice';

/**
 * Decide whether the session is still usable.
 * An EXPIRED access token does NOT mean logged out: if a refresh token is
 * present, the axios response interceptor will silently refresh + retry on the
 * first protected API call. So we only treat the session as dead when there is
 * neither a valid access token NOR a refresh token to recover from.
 */
export const isSessionRecoverable = () => {
  const localToken = getToken();
  if (localToken && !isTokenExpired(localToken)) return true; // valid access token
  if (getRefreshToken()) return true; // access token gone/expired but refreshable
  return false;
};

const ProtectedRoute = ({ children, allowGuest = false }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  // Get auth token from Redux state
  const { token: reduxToken } = useSelector((state) => state.auth);

  // ✅ KEY FIX: Use a lazy initializer so the auth check runs SYNCHRONOUSLY
  // during the very first render — before anything is painted to the screen.
  // This completely prevents the flash-to-login on page reload.
  const [isValid, setIsValid] = useState(() => isSessionRecoverable());

  // Re-check authentication whenever the path changes (for navigation within the app).
  // We don't need to re-check on mount because the lazy initializer already did it.
  useEffect(() => {
    if (isSessionRecoverable()) {
      setIsValid(true);
      return;
    }

    // No valid access token and no refresh token — the session is truly over.
    clearAuth();
    dispatch(resetAuthState());
    setIsValid(false);
  }, [dispatch, location.pathname]);

  // If not valid, either redirect to login or, for routes that opted into
  // guest access (allowGuest), render the page anyway — the page itself is
  // responsible for gating any auth-only actions behind a login prompt.
  if (!isValid) {
    if (allowGuest) {
      return children;
    }

    // Construct the full path with query parameters
    const fullPath = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(fullPath)}`}
        replace
        state={{ from: location }}
      />
    );
  }

  // User has valid token - render protected component
  return children;
};

export default ProtectedRoute;